import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { DataTable, type Column } from '../components/DataTable';
import { StagePill } from '../components/DealBits';
import { LeaseCalculator } from '../components/LeaseCalculator';
import { NewDealForm } from '../components/NewDealForm';
import { Button, cx, ErrorBox, Modal, PageHeader, Pill, SearchInput, Select, Spinner } from '../components/ui';
import { useUserId } from '../lib/auth';
import { DEAL_TYPES } from '../lib/commission/stages';
import { syncDeal } from '../lib/dealEngine';
import { DEAL_TYPE_LABEL } from '../lib/deals';
import { dealPreview, type KeyDate, type PreviewPayment } from '../lib/dealPreview';
import { date, money, personName, todayISO } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';

async function loadPipeline() {
  const [deals, summary, payments, stages] = await Promise.all([
    supabase
      .from('deals')
      .select(
        'id, name, deal_type, stage, expected_close_date, next_step, next_step_date, updated_at, lease_executed_date, commencement_estimated_date, commencement_confirmed_date, contract_effective_date, dd_end_date, closing_date, contacts!deals_primary_contact_id_fkey(id, first_name, last_name, display_name), organizations(id, name)',
      )
      .order('updated_at', { ascending: false }),
    supabase.from('deal_payment_summary').select('*'),
    supabase.from('payments').select('deal_id, status, expected_date, slot_key'),
    supabase.from('deal_stages').select('deal_type, stage_key, label, probability, sort_order, is_closed').order('sort_order'),
  ]);
  const sums = new Map(unwrap(summary).map((s) => [s.deal_id, s]));
  const byDeal = new Map<string, PreviewPayment[]>();
  for (const p of unwrap(payments)) byDeal.set(p.deal_id, [...(byDeal.get(p.deal_id) ?? []), p]);
  const today = todayISO();
  return {
    deals: unwrap(deals).map((d) => ({ ...d, summary: sums.get(d.id), preview: dealPreview(d, byDeal.get(d.id) ?? [], today) })),
    stages: unwrap(stages),
  };
}
type Deal = Awaited<ReturnType<typeof loadPipeline>>['deals'][number];

/** Deal-type tabs in the saved order; types missing from the saved list keep their default place at the end. */
export function orderTabs(saved: readonly string[] | null | undefined): typeof DEAL_TYPES {
  const rank = (key: string) => {
    const i = (saved ?? []).indexOf(key);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return DEAL_TYPES.map((t, i) => ({ t, i })).sort((a, b) => rank(a.t.key) - rank(b.t.key) || a.i - b.i).map(({ t }) => t);
}

/** Moves `key` to where `target` is. */
export function moveTab(order: readonly string[], key: string, target: string): string[] {
  const from = order.indexOf(key);
  const to = order.indexOf(target);
  if (from === -1 || to === -1 || from === to) return [...order];
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, key);
  return next;
}

export function Pipeline() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['pipeline'], queryFn: loadPipeline });
  const userId = useUserId();
  // Always open on the global view.
  const [view, setView] = useState<string>('table');
  const [q, setQ] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [draggingTab, setDraggingTab] = useState<string | null>(null);

  const { data: tabOrder } = useQuery({
    queryKey: ['app-settings', 'pipeline-tabs'],
    queryFn: async () => unwrap(await supabase.from('app_settings').select('pipeline_tab_order').eq('owner_id', userId).maybeSingle())?.pipeline_tab_order ?? null,
  });
  const tabs = orderTabs(tabOrder);
  const saveTabs = useMutation({
    mutationFn: async (order: string[]) => unwrap(await supabase.from('app_settings').upsert({ owner_id: userId, pipeline_tab_order: order }, { onConflict: 'owner_id' })),
    onMutate: (order) => qc.setQueryData(['app-settings', 'pipeline-tabs'], order),
    onSettled: () => qc.invalidateQueries({ queryKey: ['app-settings'] }),
  });
  function dropTab(target: string) {
    const key = draggingTab;
    setDraggingTab(null);
    if (!key || key === target) return;
    saveTabs.mutate(moveTab(tabs.map((t) => t.key), key, target));
  }

  const move = useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: string }) => {
      unwrap(await supabase.from('deals').update({ stage }).eq('id', id));
      await syncDeal(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline'] }),
    onError: () => qc.invalidateQueries({ queryKey: ['pipeline'] }),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.deals ?? []).filter(
      (d) =>
        !needle ||
        [d.name, d.organizations?.name, d.contacts ? personName(d.contacts) : ''].some((s) => s?.toLowerCase().includes(needle)),
    );
  }, [data, q]);

  if (isLoading) return <Spinner />;
  const stageOf = (type: string) => (data?.stages ?? []).filter((s) => s.deal_type === type);

  function drop(stageKey: string, closing: boolean) {
    const deal = filtered.find((d) => d.id === dragging);
    setDragging(null);
    if (!deal || deal.stage === stageKey) return;
    if (closing && deal.summary && Number(deal.summary.payment_count) > 0 && !deal.summary.fully_paid) {
      alert(`"${deal.name}" can't close yet: not every payment is marked received. Mark them received on the deal page first.`);
      return;
    }
    // Optimistic: move the card now, then save.
    qc.setQueryData(['pipeline'], (old: typeof data) => old && { ...old, deals: old.deals.map((d) => (d.id === deal.id ? { ...d, stage: stageKey } : d)) });
    move.mutate({ id: deal.id, stage: stageKey });
  }

  return (
    <div>
      <PageHeader
        title="Pipeline"
        actions={
          <>
            <Button onClick={() => setCalculating(true)}>
              <Calculator className="h-4 w-4" /> Lease Calculator
            </Button>
            <Button variant="primary" onClick={() => setCreating(true)}>
              New deal
            </Button>
          </>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
          {[{ key: 'table', label: 'All deals (table)' }, ...tabs].map((t) => {
            const movable = t.key !== 'table';
            return (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                draggable={movable}
                onDragStart={movable ? (e) => (e.dataTransfer.setData('text/plain', t.key), setDraggingTab(t.key)) : undefined}
                onDragEnd={() => setDraggingTab(null)}
                onDragOver={(e) => draggingTab && movable && e.preventDefault()}
                onDrop={(e) => (e.preventDefault(), movable && dropTab(t.key))}
                title={movable ? 'Drag to reorder' : undefined}
                className={cx(
                  'rounded-md px-3 py-1 text-sm font-medium',
                  view === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
                  movable && 'cursor-grab active:cursor-grabbing',
                  draggingTab === t.key && 'opacity-40',
                )}
              >
                {t.label}
                <span className="ml-1 text-xs text-slate-400">{t.key === 'table' ? filtered.length : filtered.filter((d) => d.deal_type === t.key).length}</span>
              </button>
            );
          })}
        </div>
        <SearchInput value={q} onChange={setQ} placeholder="Filter by deal, person, company…" className="!w-64" />
        {view !== 'table' && (
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed
          </label>
        )}
      </div>
      <ErrorBox error={error ?? move.error ?? saveTabs.error} />

      {view === 'table' ? (
        <DealTable deals={filtered} stages={data?.stages ?? []} onOpen={(id) => navigate(`/deals/${id}`)} />
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {stageOf(view)
            .filter((s) => showClosed || !s.is_closed)
            .map((s) => {
              const cards = filtered.filter((d) => d.deal_type === view && d.stage === s.stage_key);
              const sum = cards.reduce((t, d) => t + Number(d.summary?.total_commission ?? 0), 0);
              return (
                <div
                  key={s.stage_key}
                  className={cx('flex w-64 shrink-0 flex-col rounded-lg bg-slate-100/80', dragging && 'ring-1 ring-indigo-200')}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => drop(s.stage_key, s.is_closed)}
                >
                  <div className="flex items-center justify-between px-3 py-2">
                    <StagePill stage={s.stage_key} label={s.label} />
                    <span className="text-xs text-slate-500">
                      {cards.length} · {money(sum, false)}
                    </span>
                  </div>
                  <div className="flex min-h-24 flex-col gap-2 px-2 pb-2">
                    {cards.map((d) => (
                      <DealCard key={d.id} deal={d} probability={Number(s.probability)} onDragStart={() => setDragging(d.id)} />
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      )}
      <Modal title="New deal" open={creating} onClose={() => setCreating(false)}>
        <NewDealForm />
      </Modal>
      <Modal title="Lease Calculator" open={calculating} onClose={() => setCalculating(false)} wide="xl">
        <LeaseCalculator />
      </Modal>
    </div>
  );
}

function DealCard({ deal, probability, onDragStart }: { deal: Deal; probability: number; onDragStart: () => void }) {
  const s = deal.summary;
  const today = todayISO();
  const overdue = Number(s?.overdue_count ?? 0) > 0;
  const stepLate = deal.next_step_date && deal.next_step_date < today;
  return (
    <Link
      to={`/deals/${deal.id}`}
      draggable
      onDragStart={onDragStart}
      className="block rounded-md border border-slate-200 bg-white p-2.5 text-sm shadow-sm hover:border-indigo-300"
    >
      <div className="font-medium text-slate-900">{deal.name}</div>
      <div className="mt-0.5 truncate text-xs text-slate-500">{[deal.contacts ? personName(deal.contacts) : null, deal.organizations?.name].filter(Boolean).join(' · ')}</div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-800" title={`Weighted at ${Math.round(probability * 100)}%: ${money(Number(s?.projected_total ?? 0) * probability, false)}`}>
          {Number(s?.total_commission) ? money(s?.total_commission, false) : <span className="font-normal text-slate-400">no commission yet</span>}
        </span>
        {s?.next_payment_date && <span className="text-slate-500">next {date(s.next_payment_date)}</span>}
      </div>
      {(overdue || stepLate || deal.next_step) && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {overdue && <Pill color="red">payment overdue</Pill>}
          {stepLate && <Pill color="orange">next step late</Pill>}
          {deal.next_step && !stepLate && <span className="truncate text-xs text-slate-500">→ {deal.next_step}</span>}
        </div>
      )}
    </Link>
  );
}

function DealTable({ deals, stages, onOpen }: { deals: Deal[]; stages: { deal_type: string; stage_key: string; label: string; probability: number }[]; onOpen: (id: string) => void }) {
  const [type, setType] = useState('');
  const [stage, setStage] = useState('');
  const label = (d: Deal) => stages.find((s) => s.deal_type === d.deal_type && s.stage_key === d.stage)?.label ?? d.stage;
  const rows = deals.filter((d) => (!type || d.deal_type === type) && (!stage || label(d) === stage));
  const keyDate = (k: KeyDate) =>
    k.date ? (
      <span className={cx('whitespace-nowrap', k.estimated && 'text-slate-400')} title={k.estimated ? 'Estimated / default — not entered yet' : undefined}>
        {date(k.date)}
        {k.estimated && ' (est.)'}
      </span>
    ) : (
      <span className="text-slate-300">—</span>
    );
  const columns: Column<Deal>[] = [
    { key: 'name', header: 'Deal', sortValue: (d) => d.name.toLowerCase(), render: (d) => <span className="font-medium text-slate-900">{d.name}</span> },
    { key: 'type', header: 'Type', sortValue: (d) => DEAL_TYPE_LABEL[d.deal_type], render: (d) => <Pill>{DEAL_TYPE_LABEL[d.deal_type]}</Pill> },
    {
      key: 'contact',
      header: 'Contact',
      sortValue: (d) => (d.contacts ? personName(d.contacts) : ''),
      render: (d) => (d.contacts ? personName(d.contacts) : <span className="font-medium text-amber-600">No contact</span>),
    },
    { key: 'stage', header: 'Stage', sortValue: (d) => label(d), render: (d) => <StagePill stage={d.stage} label={label(d)} /> },
    {
      key: 'first',
      header: (
        <span title="Sale / Acquisition / Buyside: due diligence (inspection period) ends. Lease: lease signed.">
          DD Expire / Lease Sign
        </span>
      ),
      sortValue: (d) => d.preview.first.date,
      render: (d) => keyDate(d.preview.first),
    },
    {
      key: 'second',
      header: <span title="Sale / Acquisition / Buyside: closing. Lease: commencement.">Closing Date / Lease Commencement</span>,
      sortValue: (d) => d.preview.second.date,
      render: (d) => keyDate(d.preview.second),
    },
    {
      key: 'comm',
      header: 'Commission',
      sortValue: (d) => Number(d.summary?.total_commission ?? 0),
      render: (d) =>
        Number(d.summary?.total_commission) ? (
          <span className={cx('font-medium', d.preview.next?.due ? 'text-rose-600' : 'text-slate-800')}>{money(d.summary?.total_commission, false)}</span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
      className: 'text-right',
    },
    {
      key: 'next',
      header: 'Next Payment',
      sortValue: (d) => d.preview.next?.date ?? (d.preview.fullyPaid ? '9999-12-31' : null),
      render: (d) =>
        d.preview.fullyPaid ? (
          <Pill color="green">paid</Pill>
        ) : d.preview.next?.date ? (
          <span className={cx('whitespace-nowrap', d.preview.next.due && 'font-semibold text-rose-600')} title={d.preview.next.due ? 'Not marked received yet' : undefined}>
            {date(d.preview.next.date)}
            {d.preview.next.note && <span className={cx('ml-1 text-xs', d.preview.next.due ? 'text-rose-500' : 'text-slate-500')}>({d.preview.next.note})</span>}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
  ];
  return (
    <div>
      <div className="mb-2 flex gap-2">
        <Select value={type} onChange={(e) => setType(e.target.value)} className="!w-48">
          <option value="">All types</option>
          {DEAL_TYPES.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </Select>
        <Select value={stage} onChange={(e) => setStage(e.target.value)} className="!w-48">
          <option value="">All stages</option>
          {[...new Set(stages.map((s) => s.label))].map((l) => (
            <option key={l}>{l}</option>
          ))}
        </Select>
      </div>
      <DataTable rows={rows} columns={columns} rowKey={(d) => d.id} onRowClick={(d) => onOpen(d.id)} empty="No deals yet — create one from a contact or with “New deal”." />
    </div>
  );
}
